import { useContext, useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import Loader from "carbon-react/esm/components/loader/__next__";
import Message from "carbon-react/esm/components/message";
import Textarea from "carbon-react/esm/components/textarea";
import Textbox from "carbon-react/esm/components/textbox";
import { getPostingView, performPostingAction } from "../api/postingApi";
import { EmbeddedSessionContext } from "../auth/EmbeddedSessionProvider";
import { DateValue, FinancialAmount } from "../components/FinancialValue";
import { MetricCard } from "../components/MetricCard";
import { SageJournalLink } from "../components/SageJournalLink";
import { StatusPill } from "../components/StatusPill";
interface Detail {
  batch: Record<string, unknown>;
  events: Array<Record<string, unknown>>;
  failures: Array<Record<string, unknown>>;
  sageBatches: Array<Record<string, unknown>>;
}
export function PostingBatchPage({
  postingBatchId,
}: {
  postingBatchId: string;
}) {
  const { api } = useContext(EmbeddedSessionContext);
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reversalDate, setReversalDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);
  const load = () => {
    if (api)
      void getPostingView<Detail>(api, "batch", { postingBatchId })
        .then(setData)
        .catch(() => setError("Posting batch could not be loaded."));
  };
  useEffect(load, [api, postingBatchId]);
  if (!data)
    return <Loader loaderLabel="Loading posting result" size="medium" />;
  const act = async (body: Record<string, unknown>) => {
    if (!api) return;
    setWorking(true);
    try {
      if (body.action === "create_reversal_preview") {
        const result = await performPostingAction<{ preview: { id: string } }>(
          api,
          body,
        );
        await performPostingAction(api, {
          action: "post_preview",
          previewId: result.preview.id,
        });
      } else await performPostingAction(api, body);
      load();
    } catch {
      setError("The posting action could not be completed.");
    } finally {
      setWorking(false);
    }
  };
  return (
    <div className="operational-stack">
      {error ? (
        <Message open title="Posting action failed" variant="error">
          {error}
        </Message>
      ) : null}
      <section className="metric-grid">
        <Metric label="Status">
          <StatusPill
            label={String(data.batch.status)}
            tone={
              data.batch.status === "posted"
                ? "positive"
                : data.batch.status === "failed"
                  ? "negative"
                  : "warning"
            }
          />
        </Metric>
        <Metric label="Posting date">
          <DateValue value={String(data.batch.posting_date)} />
        </Metric>
        <Metric label="Total debits">
          <FinancialAmount
            currency="USD"
            value={String(data.batch.total_debits)}
          />
        </Metric>
        <Metric label="Total credits">
          <FinancialAmount
            currency="USD"
            value={String(data.batch.total_credits)}
          />
        </Metric>
        <Metric label="Sage state">{String(data.batch.sage_state)}</Metric>
      </section>
      {["failed", "partially_posted"].includes(String(data.batch.status)) ? (
        <Button
          disabled={working}
          onClick={() => void act({ action: "retry_posting", postingBatchId })}
          variant="default"
          variantType="primary"
        >
          Retry failed events
        </Button>
      ) : null}
      <section className="operational-section">
        <h2>Sage results</h2>
        {data.sageBatches.map((batch) => {
          const recordNo = String(
            batch.sage_record_no ?? batch.sage_batch_key ?? "Sage batch",
          );
          const recordUrl =
            typeof batch.sage_record_url === "string"
              ? batch.sage_record_url
              : null;

          return (
            <p key={String(batch.id)}>
              <strong>
                {recordUrl ? (
                  <SageJournalLink href={recordUrl} journalId={recordNo} />
                ) : (
                  recordNo
                )}
              </strong>{" "}
              · <StatusPill label={String(batch.status)} />
            </p>
          );
        })}
        {data.failures.map((failure) => (
          <Message
            key={String(failure.id)}
            open
            title={String(failure.error_code)}
            variant="error"
          >
            {String(failure.sanitized_message)}
          </Message>
        ))}
      </section>
      {data.batch.status === "posted" ? (
        <section className="editor-panel">
          <h2>Reverse posting</h2>
          <div className="form-grid">
            <Textbox
              label="Open-period reversal date"
              onChange={(event) => setReversalDate(event.target.value)}
              value={reversalDate}
            />
            <Textarea
              label="Reason"
              onChange={(event) => setReason(event.target.value)}
              value={reason}
            />
          </div>
          <Button
            disabled={working || !reason}
            onClick={() =>
              void act({
                action: "create_reversal_preview",
                postingBatchId,
                reason,
                reversalDate,
              })
            }
            variant="default"
            variantType="secondary"
          >
            Preview and post reversal
          </Button>
        </section>
      ) : null}
    </div>
  );
}
function Metric({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) {
  return (
    <MetricCard
      icon={
        label.includes("date")
          ? "calendar"
          : label.includes("Total")
            ? "cash"
            : "analysis"
      }
      label={label}
    >
      {children}
    </MetricCard>
  );
}
